import React, { useEffect, useMemo, useRef, useState } from 'react';
import Calendar from 'react-calendar';
import { CalendarDays, ChevronDown } from 'lucide-react';
import 'react-calendar/dist/Calendar.css';

export default function DateSelector({ options, value, onChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const wrapRef = useRef(null);

  const toDate = (dateKey) => {
    if (dateKey === 'today') {
      return new Date();
    }
    const [day, month, year] = dateKey.split('-').map(Number);
    return new Date(year, month - 1, day);
  };

  const toDateKey = (dateObj) => {
    const dd = String(dateObj.getDate()).padStart(2, '0');
    const mm = String(dateObj.getMonth() + 1).padStart(2, '0');
    const yyyy = dateObj.getFullYear();
    const candidate = `${dd}-${mm}-${yyyy}`;
    const found = options.find((item) => item.key === candidate);
    if (found) {
      return found.key;
    }
    const today = new Date();
    const todayKey = `${String(today.getDate()).padStart(2, '0')}-${String(today.getMonth() + 1).padStart(2, '0')}-${today.getFullYear()}`;
    const todayOption = options.find((item) => item.key === 'today' || item.key === todayKey);
    return todayOption ? todayOption.key : candidate;
  };

  const allowedDateKeys = useMemo(
    () =>
      options.map((item) => {
        if (item.key === 'today') {
          const now = new Date();
          return `${String(now.getDate()).padStart(2, '0')}-${String(now.getMonth() + 1).padStart(2, '0')}-${now.getFullYear()}`;
        }
        return item.key;
      }),
    [options]
  );

  const selectedOption = options.find((item) => item.key === value) || options[0];
  const selectedDate = selectedOption ? toDate(selectedOption.key) : new Date();

  useEffect(() => {
    const onDocClick = (event) => {
      if (wrapRef.current && !wrapRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const tileDisabled = ({ date, view }) => {
    if (view !== 'month') {
      return false;
    }
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    const key = `${dd}-${mm}-${yyyy}`;
    return !allowedDateKeys.includes(key);
  };

  return (
    <div className="date-picker-wrap" ref={wrapRef}>
      <button
        type="button"
        className="date-select"
        onClick={() => setIsOpen((prev) => !prev)}
      >
        <CalendarDays size={15} />
        <span>{selectedOption ? selectedOption.label : 'Select date'}</span>
        <ChevronDown size={14} />
      </button>

      {isOpen && (
        <div className="calendar-pop">
          <Calendar
            onChange={(nextDate) => {
              onChange(toDateKey(nextDate));
              setIsOpen(false);
            }}
            value={selectedDate}
            tileDisabled={tileDisabled}
            prev2Label={null}
            next2Label={null}
            formatShortWeekday={(locale, date) =>
              date.toLocaleDateString(locale, { weekday: 'narrow' })
            }
          />
        </div>
      )}
    </div>
  );
}
